import React, { Fragment,  useEffect, useState } from 'react';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components/macro';
import { v4 as uuidv4 } from 'uuid';
import { useParams } from 'react-router';
import { Redirect } from 'react-router-dom';

import Page, { Back, CustomBack, Title } from '../../../components/Page';
import Card from '../../../components/Card';
import Location from '../containers/Location';
import Audience from './Audience';
import CampaignBasicInfo from './CampaignBasicInfo';
import { Success } from '../../common/components';
import { colors, distances } from '../../../styles/constants';
import Discount from './Discount';
import Publication from './Publication';
import { validate } from '../../validation/campaignValidation';
import { Campaign as CampaignBackendType, Discount as DiscountBackendType } from '../../../types/discounts';
import { AssetRequestObject, AudienceType, Campaign, DiscountItem, DiscountType, GroupItems, LocationType, LoyaltyDiscountType, LoyaltyType, Stage } from '../../types';
import { getLimitationByDiscountType, getRequirementsByDiscountType, getRewardByDiscountType } from '../helpers';
import { State as CampaignCreationState } from '../reducer';
import { useScopes } from '../../../auth/accessToken/Allow';
import { LoadingOverlay } from '../../../components/Loading';
import { parseDiscountToFrontendType } from '../../common/helpers';
import useHelpers from '../../common/useHelpers';
import { canWriteDiscounts } from '../../utils/accessUtils';
import { checkIsMccOnly, getStoreIds } from '../../helpers';

interface CampaignCreationProps {
    campaignCreationState: CampaignCreationState;
    createCampaign: (
        accountId: string,
        campaign: CampaignBackendType,
        discounts: DiscountBackendType[],
        customers: string[]
    ) => void;
    createDiscount: (accountId: string, discount: DiscountBackendType) => void;
    createAsset: (accountId: string, asset: AssetRequestObject) => void;
    removeAsset: (discountId: string) => void;
    fetchDiscount: (accountId: string, discountId: string) => void;
}

const getInitialLocationType = (discount: DiscountBackendType | undefined, isMccOnly: boolean): LocationType => {
    if (isMccOnly) {
        return LocationType.mcc;
    }

    if (
        (discount?.requirement.store?.mcc || []).length > 0 ||
        (discount?.requirement.store?.mcc_ranges || []).length > 0
    ) {
        return LocationType.mcc;
    }

    const locationType =
        discount?.requirement?.store_ids &&
        discount?.requirement?.store_ids.length > 0 &&
        discount?.requirement?.store_ids[0] !== '*'
            ? LocationType.predefinedLocations
            : LocationType.allLocations;
    return locationType;
};

const CampaignCreation = (
    {
        campaignCreationState,
        createCampaign,
        createAsset,
        removeAsset,
        fetchDiscount,
    }: CampaignCreationProps
) => {
    const { t } = useTranslation();
    const { accountId, discountId } = useParams();
    const helpers = useHelpers();
    const scopes = useScopes();
    const [stage, setStage] = useState<Stage>(Stage.discounts);

    useEffect(() => {
        if (accountId && discountId) {
            fetchDiscount(accountId, discountId);
        }
    }, [accountId, discountId, fetchDiscount]);
    if (!canWriteDiscounts(scopes)) {
        return <Redirect to={`/${accountId}/marketing/discounts`} />;
    }

    const { discount } = campaignCreationState;
    if (discountId && !discount) {
        return <LoadingOverlay />;
    }

    const parsedDiscount = discount ? parseDiscountToFrontendType(discount, helpers) : undefined;

    const isMccOnly = checkIsMccOnly(accountId);
    const locationType = getInitialLocationType(discount, isMccOnly);

    const allStoreIds = getStoreIds(discount);

    const storeIds =
        allStoreIds && allStoreIds[0] !== '*'
            ? allStoreIds
            : [];


    const initialValues = {
        name: '',
        dateFrom: new Date(),
        dateTo: new Date(),
        timeFrom: new Date(new Date().setHours(0, 0, 0, 0)),
        timeTo: new Date(new Date().setHours(23, 59, 59, 999)),
        visibleFrom: 0,
        discounts: parsedDiscount ? [parsedDiscount] : [],
        locationType: locationType,
        storeIds: storeIds,
        mccs: [],
        mccRanges: [],
        audienceType: AudienceType.allAudience,
        audienceUserIds: [],
        audience: [],
    } as Campaign;

    const assetsByDiscountId = campaignCreationState.assets.reduce<{ [key: string]: string }>(
        (acc, asset) => ({
            ...acc,
            [asset.discountId]: asset.publicUrl,
        }),
        {}
    );

    const isNameCompleted = (name: string) => name.length > 0;

    const getStatus = (condition: boolean, textSelector: string, count: number) =>
        `${condition ? '✓' : `${count}.`} ${t(`marketing.campaign.${textSelector}` as any)}`;


    const emptyDiscount = {
        id: uuidv4(),
        type: isMccOnly ? DiscountType.discountInPercentage : DiscountType.discountInAmount,
        loyaltyType: LoyaltyType.visitAndSpending,
        loyaltyDiscountType: LoyaltyDiscountType.inCurrency,
        discountValue: '',
        discountUsageAmount: '-1',
        discountCombination: 0,
        totalPurchase: '',
        payFor: '',
        items: [],
        groupItems: GroupItems.allItems,
        title: '',
        description: '',
        visitsNumber: '',
        minimumPurchase: '',
        requirements: [],
        itemsBought: helpers.itemsBought[0].value,
        validFor: helpers.months[0].value,
        emailSendout: false,
    } as DiscountItem;

    const onSubmit = (campaign: Campaign) => {
        switch (stage) {
            case Stage.publication:
                break;
            case Stage.discounts:
                if (campaign.discounts.length > 0) {
                    setStage(Stage.locations);
                } else {
                    campaign.discounts.push(emptyDiscount);
                    setStage(Stage.discounts);
                }
                return;
            case Stage.locations:
                setStage(Stage.publication);
                return;
            default:
                return;
        }
        const visibleFrom = new Date();
        visibleFrom.setDate(campaign.dateFrom.getDate() - campaign.visibleFrom);
        visibleFrom.setHours(campaign.timeFrom.getHours(), campaign.timeFrom.getMinutes());
        const campaignId = uuidv4();
        if (accountId) {
            const discounts = campaign.discounts.map((discount) => {
                const publicUrl = assetsByDiscountId[discount.id];
                return {
                    id: discount.id,
                    active: true,
                    private: false,
                    campaign_id: campaignId,
                    name: discount.title,
                    visible_from: visibleFrom.toISOString(),
                    description: discount.description,
                    limitation: getLimitationByDiscountType(discount),
                    requirement: getRequirementsByDiscountType(discount, campaign),
                    reward: getRewardByDiscountType(discount),
                    links: publicUrl || discount.webshop ? [
                        ...(publicUrl ? [{
                            href: publicUrl,
                            rel: 'thumbnail_discount_image',
                        }] : []),
                        ...(discount.webshop ? [{
                            href: discount.webshop,
                            rel: 'webshop',
                        }] : []),
                    ] : undefined,
                    metadata: discount.metadata_dintero_exclude_public_description ? {
                        'dintero:exclude:public:description': discount.metadata_dintero_exclude_public_description,
                    } : undefined,
                };
            });
            let customers = [];
            if (campaign.audienceType === AudienceType.allAudience) {
                customers = ['/', '*'];
            } else if (campaign.audienceType === AudienceType.predefinedAudience) {
                customers = ['*'];
            } else if (campaign.audienceType === AudienceType.userIds) {
                customers = campaign.audienceUserIds.filter(x => x).map(x => x.trim());
            } else {
                console.error('Invalid campaign audience');
                // invalid audience
                return;
            }
            createCampaign(
                accountId,
                {
                    campaign_id: campaignId,
                    name: campaign.name,
                    private: false,
                },
                discounts,
                customers
            );
            setStage(Stage.success);
            campaign.discounts = [];
        }
    };

    const createAssetRequest = (asset: AssetRequestObject) => {
        if (accountId) {
            createAsset(accountId, asset);
        }
    };

    return (
        <Page
            title={
                <Fragment>
                    {stage === Stage.discounts && <Back />}
                    {stage === Stage.locations && <CustomBack onClick={() => setStage(Stage.discounts)} />}
                    {stage === Stage.publication && <CustomBack onClick={() => setStage(Stage.locations)} />}
                    <Title>{t('marketing.campaign.campaign_creation')}</Title>
                </Fragment>
            }
        >
            <Formik initialValues={initialValues} validate={(values) => validate(values, t)} onSubmit={onSubmit}>
                {({ values, errors, handleChange, handleBlur, handleSubmit, setFieldValue, touched }) => (
                    <form onSubmit={handleSubmit}>
                        <CardsWrapper>
                            <Card
                                title={t('marketing.campaign.create_new_title')}
                                shortHr
                                marginBottom={distances.small}
                            >
                                <StatusWrapper>
                                    <Status isCompleted={isNameCompleted(values.name)}>
                                        {getStatus(isNameCompleted(values.name), 'campaign_name', 1)}
                                    </Status>
                                    <Status isCompleted={values.discounts.length > 0}>
                                        {getStatus(values.discounts.length > 0, 'add_discounts', 2)}
                                    </Status>
                                    <Status isCompleted={stage !== Stage.discounts}>
                                        {getStatus(stage !== Stage.discounts, 'select_location', 3)}
                                    </Status>
                                    <Status isCompleted={stage !== Stage.discounts}>
                                        {getStatus(stage !== Stage.discounts, 'select_audience', 4)}
                                    </Status>
                                    <Status isCompleted={stage === Stage.success}>
                                        {getStatus(stage === Stage.success, 'publish', 5)}
                                    </Status>
                                </StatusWrapper>
                            </Card>
                            {stage === Stage.discounts && (
                                <>
                                    <CampaignBasicInfo
                                        title={t('marketing.campaign.campaign_name_title')}
                                        subtitle={t('marketing.campaign.campaign_name_subtitle')}
                                        values={values}
                                        handleChange={handleChange}
                                        handleBlur={handleBlur}
                                        setFieldValue={setFieldValue}
                                        errors={errors}
                                        touched={touched}
                                    />
                                    <Discount
                                        values={values}
                                        setFieldValue={setFieldValue}
                                        handleChange={handleChange}
                                        handleBlur={handleBlur}
                                        errors={errors}
                                        touched={touched}
                                        onNextStage={() => setStage(Stage.locations)}
                                        createAsset={createAssetRequest}
                                        removeAsset={removeAsset}
                                        scopes={scopes}
                                        assetsByDiscountId={assetsByDiscountId}
                                        isMccOnly={isMccOnly}
                                        emptyDiscount={emptyDiscount}
                                    />
                                </>
                            )}
                            {stage === Stage.locations && (
                                <>
                                    <Location
                                        values={values}
                                        setFieldValue={setFieldValue}
                                        handleBlur={handleBlur}
                                        isMccOnly={isMccOnly}
                                    />
                                    <Audience
                                        values={values}
                                        setFieldValue={setFieldValue}
                                        onNextStage={() => setStage(Stage.publication)}
                                        onPreviousStage={() => setStage(Stage.discounts)}
                                    />
                                </>
                            )}
                            {stage === Stage.publication && (
                                <Publication
                                    onPreviousStage={() => setStage(Stage.locations)}
                                    isImageLoading={
                                        campaignCreationState.assets.every((asset) => asset.publicUrl.length === 0) &&
                                        campaignCreationState.isLoading
                                    }
                                />
                            )}
                            {stage === Stage.success && (
                                <Success subtitle={t('marketing.campaign.success_campaign_text')} />
                            )}
                        </CardsWrapper>
                    </form>
                )}
            </Formik>
        </Page>
    );
};

const CardsWrapper = styled.div`
    display: flex;
    flex-direction: column;
`;

const StatusWrapper = styled.div`
    display: flex;
    flex-wrap: wrap;
`;

const Status = styled.p<{ isCompleted: boolean }>`
    margin-right: ${distances.small};
    color: ${(props) => (props.isCompleted ? colors.primary : colors.text)};
    min-width: 130px;
`;

export default CampaignCreation;
